import { useId, useState, type ReactNode } from 'react'
import { CONTROL_BASE, controlTone, describedBy, FormField } from '../../components/ui'
import { cn } from '../../utils/cn'

interface PasswordInputProps {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  error?: ReactNode
  hint?: ReactNode
  autoComplete?: string
  disabled?: boolean
  required?: boolean
}

/**
 * A password field with the show/hide affordance the design puts on every
 * password screen (`set_your_password`, `create_your_password_oroshi_beauty_1`).
 *
 * Built on `FormField` plus the shared control styling rather than on `Input`, so
 * the toggle can sit vertically centred on the control itself instead of being
 * offset from the top of the whole field by a hard-coded label height.
 */
export function PasswordInput({
  label,
  value,
  onChange,
  error,
  hint,
  autoComplete = 'current-password',
  disabled,
  required,
}: PasswordInputProps) {
  const fieldId = useId()
  const [visible, setVisible] = useState(false)

  return (
    <FormField id={fieldId} label={label} hint={hint} error={error} required={required}>
      <div className="relative">
        <input
          id={fieldId}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fieldId, hint, error)}
          className={cn(CONTROL_BASE, controlTone(Boolean(error)), 'pr-12')}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-outline transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <span className="material-symbols-outlined text-[20px]">{visible ? 'visibility' : 'visibility_off'}</span>
        </button>
      </div>
    </FormField>
  )
}
